/**
 * Playground interceptor handlers for miscellaneous/common entities:
 * Tags, Roles, Platforms, User Preferences, Metrics, Permissions, Page Registry.
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const tagsStore = new PlaygroundSessionStore('pg_tags', 10);
const now = () => new Date().toISOString();

/**
 * Read a simple array from sessionStorage by key.
 */
function readSessionArray(key) {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

// --- Tags ---

/**
 * GET /v1/web/tags - list all tags.
 */
export function handleGetTags(config, match) {
    return { tags: tagsStore.getAll() };
}

/**
 * GET /v1/web/tags/:id - single tag by id.
 */
export function handleGetTagById(config, match) {
    const id = match[1];
    const item = tagsStore.getById(id);
    if (!item) {
        throw new Error('Tag with id ' + id + ' not found');
    }
    return item;
}

/**
 * POST /v1/web/tags - create new tag.
 */
export function handleCreateTag(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newTag = { ...data, id };
    return { tag: tagsStore.create(newTag) };
}

/**
 * PUT /v1/web/tags/:id - update existing tag.
 */
export function handleUpdateTag(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    return tagsStore.update(id, { ...data, id });
}

/**
 * DELETE /v1/web/tags/:id - remove tag.
 */
export function handleDeleteTag(config, match) {
    const id = match[1];
    tagsStore.delete(id);
    return { success: true };
}

// --- Roles ---

/**
 * GET /v1/web/role or /api/v1/roles - return all roles.
 */
export function handleGetRoles(config, match) {
    return readSessionArray('pg_roles');
}

// --- Platforms ---

/**
 * GET /v1/web/platforms - return all platforms.
 */
export function handleGetPlatforms(config, match) {
    return readSessionArray('pg_platforms');
}

// --- User Preferences ---

/**
 * GET /v1/web/user/user-pref-list - return empty preferences.
 */
export function handleGetUserPreferences(config, match) {
    return { preferences: [] };
}

// --- Metrics Catalogue ---

/**
 * GET /v1/web/metric-catalog - return all metrics.
 */
export function handleGetMetrics(config, match) {
    return readSessionArray('pg_metrics');
}

// --- Permissions ---

/**
 * GET /v1/web/permissions - return empty permissions list.
 */
export function handleGetPermissions(config, match) {
    return { permissions: [] };
}

// --- Page Registry ---

/**
 * GET /v1/web/page-registry or similar page management - return empty pages list.
 */
export function handleGetPages(config, match) {
    return { pages: [] };
}
